// Wire contract for the launcher HTTP API (`mini-apps/launcher/api.ts`), read by
// `api-client.ts`. Every schema here is the response of ONE route; the TS types
// are `z.infer` — `components/types.ts`, `components/waterfallTypes.ts` and
// `copy-tokens.ts` re-export them type-only (erased, so no zod reaches a
// bundle through those files).
//
// Shapes were checked against real rows (prod + staging, read-only) rather than
// the old hand-written interfaces, which had drifted:
//   - waterfall rows / template rows are unvalidated JSONB the UI wrote over
//     several versions: 150 of 2066 prod cells have no `email_type`, 108 no
//     `handoff_*`, a few no `subject`/`body`, and some carry `use_ai_notes`.
//     Defaults below are the ones the UI and `createTestDispatch` already apply
//     (`email_type ?? 'manual'`, `handoff_hours ?? 8`). Objects are LOOSE so a
//     key the UI does not model survives the edit-and-save round trip.
//   - `created_at` on a template list row is `string | null` on the wire, not
//     the `string | undefined` the old interface said.
import { z } from 'zod';

import { ChannelSchema, type Channel } from '../_shared/launcher-channel-schema';

export { ChannelSchema, type Channel };

// ── Waterfall rows ───────────────────────────────────────────────────────────

export const SenderValueSchema = z.object({
  type: z.enum(['token', 'static']),
  token: z.enum(['gtme_owner_email', 'gs_owner_email', 'claydr_email']).optional(),
  email: z.string().optional(),
});
export type SenderValue = z.infer<typeof SenderValueSchema>;

export const ConditionOperatorSchema = z.enum([
  'is_not_null',
  'is_null',
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'contains_any',
  'gt',
  'lt',
  'in_last_n_days',
  'not_in_last_n_days',
  'in_next_n_days',
  'not_in_next_n_days',
  'in_any_of',
  'not_in_any_of',
]);
export type ConditionOperator = z.infer<typeof ConditionOperatorSchema>;

export const ConditionSchema = z.looseObject({
  field: z.string(),
  operator: ConditionOperatorSchema,
  /** string for scalar operators; string[] for in_any_of / not_in_any_of */
  value: z.union([z.string(), z.array(z.string()), z.null()]),
});
export type Condition = z.infer<typeof ConditionSchema>;

const LogicSchema = z.enum(['AND', 'OR']);

export const ConditionGroupSchema = z.looseObject({
  id: z.string(),
  conditions: z.array(ConditionSchema),
  logic: LogicSchema,
});
export type ConditionGroup = z.infer<typeof ConditionGroupSchema>;

export const CopyStatusSchema = z.enum(['draft', 'ready']);
export type CopyStatus = z.infer<typeof CopyStatusSchema>;

export const EmailTypeSchema = z.enum(['manual', 'ai_generated', 'ai_generated_with_base']);
export type EmailType = z.infer<typeof EmailTypeSchema>;

const nullableSender = SenderValueSchema.nullable().default(null);

export const CellConfigSchema = z.looseObject({
  enabled: z.boolean(),
  channel: ChannelSchema,
  flow_id: z.string(),
  has_custom_copy: z.boolean().default(false),
  subject: z.string().default(''),
  body: z.string().default(''),
  copy_status: CopyStatusSchema.nullable().default(null),
  sender: nullableSender,
  cc: nullableSender,
  reply_to: nullableSender,
  has_claydr_handoff: z.boolean().default(false),
  handoff_hours: z.number().default(8),
  email_type: EmailTypeSchema.default('manual'),
});
export type CellConfig = z.infer<typeof CellConfigSchema>;

export const WaterfallRowSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  conditions: z.array(ConditionSchema).default([]),
  condition_logic: LogicSchema.default('AND'),
  condition_groups: z.array(ConditionGroupSchema).default([]),
  group_logic: LogicSchema.default('AND'),
  follow_up_description: z.string().default(''),
  sender: nullableSender,
  cc: nullableSender,
  reply_to: nullableSender,
  cells: z.record(z.string(), CellConfigSchema),
});
export type WaterfallRow = z.infer<typeof WaterfallRowSchema>;
export const WaterfallRowsSchema = z.array(WaterfallRowSchema);

/**
 * Repair view of a stored row: identical, except a cell's `channel` may be any
 * string. The Template Admin editor opens rows through this so it can show (and
 * let an admin fix) a channel the strict schema rejects; Save then goes back
 * through `WaterfallRowsSchema`.
 */
export const RepairableWaterfallRowSchema = WaterfallRowSchema.extend({
  cells: z.record(z.string(), CellConfigSchema.extend({ channel: z.string() })),
});
export type RepairableWaterfallRow = z.infer<typeof RepairableWaterfallRowSchema>;
export const RepairableWaterfallRowsSchema = z.array(RepairableWaterfallRowSchema);

/** Scorecard column -> raw SFDC statuses that map to it. */
export const StatusMappingSchema = z.record(z.string(), z.array(z.string()));
export type StatusMapping = z.infer<typeof StatusMappingSchema>;

// ── Mutation acks ────────────────────────────────────────────────────────────
// Acks nobody reads (`{ ok }`, the PATCH/PUT echoes) are not parsed at all: the
// route has already committed the write, so a rejected ack would report failure
// for work that happened. Only responses a caller reads have a schema here.

export const WipeDispatchLogSchema = z.object({ ok: z.boolean(), deleted: z.number() });

// ── Templates / enrichment flags ─────────────────────────────────────────────

export const ScorecardTemplateSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  campaign_type: z.string(),
  campaign_subtype: z.string().nullable().default(null),
  is_default: z.boolean().default(false),
  cm_status_columns: z.array(z.string()).default([]),
  /** Raw template rows — `templateToRows()` normalizes them; unvalidated JSONB. */
  rows: z.array(z.unknown()),
  created_at: z.string().nullish(),
  source: z.enum(['template', 'launch']).optional(),
  campaign_sfdc_id: z.string().nullish(),
  campaign_name: z.string().nullish(),
});
export type ScorecardTemplate = z.infer<typeof ScorecardTemplateSchema>;
export const ScorecardTemplatesSchema = z.array(ScorecardTemplateSchema);

export const EnrichmentFlagSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  condition: ConditionSchema,
  type: z.enum(['warning', 'exclusion']),
  order: z.number(),
});
export type EnrichmentFlag = z.infer<typeof EnrichmentFlagSchema>;
export const EnrichmentFlagsSchema = z.array(EnrichmentFlagSchema);

// ── Campaigns / waterfall ────────────────────────────────────────────────────

export const CampaignStatusesSchema = z.array(z.string());

export const LaunchedCampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string().nullable(),
  launcher_status: z.string(),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
});
export const LaunchedCampaignsSchema = z.array(LaunchedCampaignSchema);

/** `GET .../waterfall` — `rows` / `status_mapping` are null for a never-saved campaign. */
export const WaterfallResponseSchema = z.object({
  rows: WaterfallRowsSchema.nullable(),
  status_mapping: StatusMappingSchema.nullable(),
});

/** `PUT .../waterfall` echoes the saved rows. */
export const SaveWaterfallResponseSchema = z.object({
  rows: WaterfallRowsSchema,
  status_mapping: StatusMappingSchema.nullable(),
});

export const WaterfallConfigRowSchema = z.object({
  campaign_sfdc_id: z.string(),
  launcher_status: z.string(),
  /** Unvalidated JSONB, like template `rows`: the admin page exists to inspect rows that may be corrupt. */
  waterfall_rows: z.array(z.unknown()).nullable(),
  status_mapping: StatusMappingSchema.nullable(),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
});
export type WaterfallConfigRow = z.infer<typeof WaterfallConfigRowSchema>;
export const WaterfallConfigsSchema = z.array(WaterfallConfigRowSchema);

// ── Dispatch ─────────────────────────────────────────────────────────────────

export const LaunchDispatchResponseSchema = z.object({
  dispatch_id: z.string(),
  queued: z.number(),
  start_failed: z.number().optional(),
  skipped: z.number(),
  skip_reasons: z.object({ unmapped_status: z.number(), no_row: z.number(), cell_disabled: z.number() }),
});

export const RelaunchResponseSchema = z.object({
  dispatch_id: z.string(),
  dispatched: z.number(),
  skipped: z.number(),
  sent: z.number(),
  failed: z.number(),
});

export const TestDispatchResponseSchema = z.object({
  dispatched: z.number(),
  results: z.array(
    z.object({ member_id: z.string(), status: z.enum(['sent', 'error']), error: z.string().optional() }),
  ),
});

export const ReenrichResponseSchema = z.object({ dispatched: z.number() });

export const DispatchLogRowSchema = z.looseObject({
  id: z.string(),
  dispatch_id: z.string(),
  campaign_id: z.string(),
  campaign_member_id: z.string(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable(),
  contact_email: z.string().nullable(),
  bucket: z.string().nullable(),
  status_column: z.string().nullable(),
  flow_id: z.string().nullable(),
  channel: z.string().nullable(),
  subject: z.string().nullable(),
  body: z.string().nullable(),
  sender_email: z.string().nullable(),
  is_test: z.boolean(),
  result: z.enum(['queued', 'processing', 'sent', 'skipped', 'failed']),
  error: z.string().nullable(),
  processing_at: z.string().nullable(),
  dispatched_at: z.string(),
  result_status: z.string().nullable(),
  result_at: z.string().nullable(),
  result_data: z.record(z.string(), z.unknown()).nullable(),
});
export type DispatchLogRow = z.infer<typeof DispatchLogRowSchema>;
export const DispatchLogSchema = z.array(DispatchLogRowSchema);

// ── Sync / settings ──────────────────────────────────────────────────────────

export const CampaignTypeConfigSchema = z.object({
  id: z.string(),
  type: z.string(),
  subtype: z.string().nullable(),
  enabled: z.boolean(),
});
export type CampaignTypeConfig = z.infer<typeof CampaignTypeConfigSchema>;
export const CampaignTypeConfigsSchema = z.array(CampaignTypeConfigSchema);

export const WebhookSettingsSchema = z.object({
  launch_webhook_url: z.string().nullable(),
  reenrich_webhook_url: z.string().nullable(),
  add_member_webhook_url: z.string().nullable(),
  campaign_view_webhook_url: z.string().nullable(),
});
export type WebhookSettings = z.infer<typeof WebhookSettingsSchema>;
